import { type JSX, useEffect } from 'react';
import { useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { getAutocompleteOnChange } from '../../lib/ get-autocomplete-on-change';
import { searchPeople, searchPeopleQuery, resetData } from './model';
import type { People } from './types';

export const PeopleSearchControl = withControl<{
  placeholder?: string;
  multiple?: boolean;
}>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    placeholder,
    multiple,
    helperText,
  }) => {
    const { data, pending } = useUnit(searchPeopleQuery);

    useEffect(() => () => resetData(), []);

    const handleInputChange = (_: unknown, value: string, reason: string) => {
      if (reason === 'input') {
        searchPeople(value);
      }

      return resetData();
    };

    const handleChange = (
      _: unknown,
      newValue: string | People | (string | People)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <FormControl disabled={disabled}>
        {label && (
          <FormLabel required={!readOnly && required} error={isError}>
            {label}
          </FormLabel>
        )}
        <Autocomplete
          getOptionKey={(option) => option.id}
          multiple={multiple}
          disableCloseOnSelect={multiple}
          loading={pending}
          value={value ?? null}
          options={Array.isArray(data) ? data : []}
          onChange={handleChange}
          onInputChange={handleInputChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  placeholder,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
        {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);
